import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import BlogSidebar from "../components/BlogSidebar";
import ArticleLayout from "../components/ArticleLayout";
import SEOHead from "../components/SEOHead";
import { getPostBySlug } from "../data/blogPosts";
import { useTranslation } from "react-i18next";

export default function BlogPost() {
  const { t } = useTranslation();
  const { slug } = useParams();
  const post = getPostBySlug(slug);
  const [content, setContent] = useState('');

  useEffect(() => {
    if (post) {
      import("../data/blogContent").then(({ getContentBySlug }) => {
        const fetched = getContentBySlug(slug);
        setContent(fetched || '');
      });
    }
  }, [slug, post]);

  if (!post) {
    return (
      <div className="min-h-screen bg-[#09090B] text-white">
        <Header />
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20 text-center">
          <h1 className="font-display text-4xl uppercase mb-4">
            {t("blog_post.article_not_found")}
          </h1>
          <p className="text-zinc-400 mb-8">
            {t("blog_post.the_article_youre_looking_for_doesnt_exist_or_has")}
          </p>
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-black px-6 py-3 font-bold uppercase tracking-wider text-xs transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Blog
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const articleStructuredData = post
    ? {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: post.title,
        description: post.excerpt,
        image: post.ogImage,
        datePublished: post.date,
        dateModified: post.date,
        author: {
          "@type": "Person",
          name: post.author?.name || "Max Employee",
          url: "https://maxemployee.com",
        },
        publisher: {
          "@type": "Organization",
          name: "Max Employee",
          logo: {
            "@type": "ImageObject",
            url: "https://maxemployee.com/og-image-homepage.webp",
          },
        },
        mainEntityOfPage: {
          "@type": "WebPage",
          "@id": `https://maxemployee.com/blog/${slug}`,
        },
        keywords: (post.tags || []).join(", "),
      }
    : null;

  const breadcrumbStructuredData = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: "https://maxemployee.com/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Blog",
        item: "https://maxemployee.com/blog",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: post.title,
        item: `https://maxemployee.com/blog/${slug}`,
      },
    ],
  };

  return (
    <div className="min-h-screen bg-[#09090B] text-white">
      {post && (
        <SEOHead
          title={`${post.title} | Max Employee Blog`}
          description={post.excerpt}
          image={post.ogImage}
          url={`/blog/${slug}`}
          type="article"
          publishedTime={post.date}
          modifiedTime={post.date}
          tags={post.tags}
          twitterCreator="@maxemployee"
          structuredData={[articleStructuredData, breadcrumbStructuredData]}
        />
      )}
      <Header />
      <main id="main-content">
        <ArticleLayout post={post} slug={slug} content={content} />
      </main>

      <div
        className="lg:hidden max-w-7xl mx-auto px-6 lg:px-12 py-8 border-t border-zinc-900"
        aria-label={t("blog_post.blog_sidebar")}
      >
        <BlogSidebar />
      </div>

      <Footer />
    </div>
  );
}
